import type { ExperienceItem } from "@/lib/types";
import { translate } from "@/lib/i18n/useI18n";
import type { Language } from "@/lib/i18n/languages";

// ─── Marketplace max-price filter (Marketplace Filters V2) ─────────────────
//
// Product direction: the price control in the Experiences marketplace means
// "show me activities up to this maximum amount", not a total-trip budget
// (that concept still exists separately in Planner via FilterSidebar, which
// this module and its UI consumers deliberately do NOT touch). A null
// maxPrice always means "Sin límite" — no filtering applied.

export const PRICE_FILTER_UI_CAP = 5000;

/** Every step a quick chip may offer, in ascending order. "Sin límite" (null) is separate. */
export const PRICE_CHIP_STEPS: readonly number[] = [30, 50, 100, 250];

export interface PriceChip {
  label:    string;
  value:    number | null; // null = "Sin límite"
  count:    number;        // items that would remain visible if this chip is selected
  disabled: boolean;       // no items qualify — shown grayed out, not hidden
}

/**
 * Real, numeric price_from values only — excludes null/undefined (no price
 * data at all). Keeps 0 (a genuinely free activity is a meaningful price
 * point, not "missing data").
 */
export function getPricedValues(items: readonly ExperienceItem[]): number[] {
  return items
    .map(e => e.price_from)
    .filter((p): p is number => typeof p === "number" && Number.isFinite(p) && p >= 0);
}

/** True once there's enough real price data to make a price filter meaningful. */
export function hasEnoughPricedItems(items: readonly ExperienceItem[]): boolean {
  return getPricedValues(items).length >= 2;
}

/**
 * Rounds up to a "clean" step so the slider/axis never shows an odd number
 * like $187 — the exact bucket size grows with the value so small catalogs
 * get fine-grained control and large ones stay readable.
 */
export function roundUpToCleanStep(value: number): number {
  if (value <= 0)    return 0;
  if (value <= 100)  return Math.ceil(value / 10) * 10;
  if (value <= 500)  return Math.ceil(value / 25) * 25;
  if (value <= 1000) return Math.ceil(value / 100) * 100;
  if (value <= 2500) return Math.ceil(value / 250) * 250;
  return Math.ceil(value / 500) * 500;
}

/**
 * The dynamic slider/range maximum for the current marketplace inventory.
 * Returns null when there isn't enough real price data to justify showing a
 * price control at all (caller should hide/disable it and show
 * "Sin precios suficientes").
 */
export function computeDynamicMaxPrice(items: readonly ExperienceItem[]): number | null {
  const values = getPricedValues(items);
  if (values.length < 2) return null;
  const observedMax = Math.max(...values);
  return Math.min(roundUpToCleanStep(observedMax), PRICE_FILTER_UI_CAP);
}

/**
 * Quick "Hasta $X" chips, filtered down to only the ones that make sense for
 * this inventory:
 *   - A step chip is omitted entirely when it exceeds the dynamic max (e.g.
 *     never offer "Hasta $250" when nothing here costs more than $50 — it
 *     would be identical to "Sin límite").
 *   - A step chip with zero qualifying items is kept but marked `disabled`
 *     (informative — "nothing this cheap exists" — rather than vanishing).
 * "Sin límite" is always included, with its count set to the full inventory
 * size (not just the priced subset), matching what selecting it actually
 * restores.
 */
// Sprint: Frontend UI i18n Foundation V1, Parte C — `language` is an
// OPTIONAL 3rd parameter defaulting to "es", so every EXISTING call site/
// test (priceFilter.test.ts) keeps getting byte-for-byte the same Spanish
// labels; only a caller that explicitly passes a language gets translated
// chip text.
export function buildPriceChips(
  items:      readonly ExperienceItem[],
  dynamicMax: number | null,
  language:   Language = "es",
): PriceChip[] {
  const values = getPricedValues(items);
  const chips: PriceChip[] = [];
  const upTo = translate(language, "marketplace.filters.upToPrefix");

  for (const step of PRICE_CHIP_STEPS) {
    if (dynamicMax != null && step > dynamicMax) continue;
    const count = values.filter(v => v <= step).length;
    chips.push({ label: `${upTo} $${step}`, value: step, count, disabled: count === 0 });
  }

  chips.push({ label: translate(language, "marketplace.filters.noLimit"), value: null, count: items.length, disabled: false });
  return chips;
}

/**
 * fixed / tip_based / unknown — Marketplace Continuity, Honest Pricing &
 * Log Privacy V1, Parte B. Mirrors engine/supply_integration/
 * marketplace_search_filters.py's classify_price_type() exactly, so the
 * two never drift: "no confirmed fixed price" (unknown) and "voluntary
 * contribution" (tip_based, e.g. GuruWalk) must never be conflated.
 */
export type PriceType = "fixed" | "tip_based" | "unknown";

export function classifyPriceType(exp: ExperienceItem): PriceType {
  if (exp.pricing_model === "tip_based") return "tip_based";
  if (typeof exp.price_from === "number" && Number.isFinite(exp.price_from)) return "fixed";
  return "unknown";
}

/**
 * Applies the max-price rule to a single item. Exported standalone (not just
 * folded into a bigger filter function) so the exact rule is unit-testable
 * and reused identically by every filter panel.
 *
 * Rules:
 *   - maxPrice === null → always passes (no filter active).
 *   - Free items (price_from === 0) always pass any positive ceiling.
 *   - price_type === "unknown" → hidden once a ceiling is active,
 *     regardless of includeTipBased — an unresolved fixed price is never
 *     assumed to qualify.
 *   - price_type === "tip_based" → hidden once a ceiling is active UNLESS
 *     the caller explicitly opted in via includeTipBased (the "Incluir
 *     experiencias de aporte voluntario" control) — an explicit inclusion,
 *     never a claim that the item is confirmed to fit under the ceiling.
 */
export function passesMaxPrice(
  exp: ExperienceItem,
  maxPrice: number | null,
  includeTipBased: boolean = false,
): boolean {
  if (maxPrice === null) return true;
  const priceType = classifyPriceType(exp);
  if (priceType === "tip_based") return includeTipBased;
  if (priceType !== "fixed") return false;
  return exp.price_from! <= maxPrice;
}
